'use client';

import { SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms/icon';
import { BurgerMenuIcon } from '@/shared-ui/icons';
import { Nav } from '@/shared-ui/organisms/nav';
import { useState, type RefObject } from 'react';
import { SubmenuPanel, type SubmenuPanelItem } from '../../molecules/submenu-panel';
import { Button } from '@/components/ui/button';
import { Link } from '@/shared-ui/atoms';

export type MegaMenuNavItem = {
    id: string;
    label: string;
    to: string;
    link?: boolean;
    /** Present on merchant-configured custom mega menu links (slots 1–3). */
    customSlot?: 1 | 2 | 3;
};

type NavContentProps = {
    items: MegaMenuNavItem[];
    activeDesktopItem?: string;
    setActiveDesktopItem: (itemId: string) => void;
    closeSubmenuPanel: () => void;
    submenuPanels: SubmenuPanelItem[];
    shopAllLabel: string;
    ignorePanelCloseRefs?: Array<RefObject<HTMLElement | null>>;
    /** Condensed header: the bar sits on white, so the desktop colours invert. */
    condensed?: boolean;
};

export const NavContent = ({
    items,
    activeDesktopItem,
    setActiveDesktopItem,
    closeSubmenuPanel,
    submenuPanels,
    shopAllLabel,
    ignorePanelCloseRefs,
    condensed = false,
}: NavContentProps) => {
    const [navAnchor, setNavAnchor] = useState<HTMLDivElement | null>(null);
    const desktopItemText = condensed ? 'lg:text-snk-black' : 'lg:text-snk-white';

    return (
        <Nav.Root ref={setNavAnchor} className={cn('relative w-full', condensed && 'lg:bg-transparent lg:px-0')}>
            <Nav.Content>
                <Nav.Item>
                    <SheetTrigger asChild>
                        <Button
                            aria-label="Open Menu"
                            variant="ghost"
                            className={cn(
                                'text-snk-black gap-2 !p-0 h-auto cursor-pointer font-light text-base hover:bg-snk-primary hover:font-normal'
                            )}>
                            <Icon
                                icon={BurgerMenuIcon}
                                className={cn('text-snk-black size-8 lg:size-6', desktopItemText)}
                                decorative
                            />
                            <span
                                className={cn(
                                    'hidden lg:block hover:opacity-90 hover:bg-snk-primary',
                                    desktopItemText
                                )}>
                                {shopAllLabel}
                            </span>
                        </Button>
                    </SheetTrigger>
                </Nav.Item>

                {items.map((item) => (
                    <Nav.Item key={item.id} desktopOnly>
                        {item.link === false ? (
                            <button
                                type="button"
                                onClick={() => setActiveDesktopItem(item.id)}
                                className={cn(
                                    'inline-flex items-center justify-center cursor-pointer',
                                    condensed ? 'text-snk-black' : 'text-snk-white'
                                )}>
                                {item.label}
                            </button>
                        ) : (
                            <Link
                                to={item.to}
                                onClick={closeSubmenuPanel}
                                className={cn(
                                    'inline-flex items-center justify-center',
                                    condensed ? 'text-snk-black' : 'text-snk-white'
                                )}>
                                {item.label}
                            </Link>
                        )}
                    </Nav.Item>
                ))}
            </Nav.Content>

            <SubmenuPanel
                activeItemKey={activeDesktopItem}
                panels={submenuPanels}
                onClose={closeSubmenuPanel}
                ignoreRefs={ignorePanelCloseRefs}
                anchorElement={navAnchor}
            />
        </Nav.Root>
    );
};
